/**
 * Manage a STANDALONE (credential) pool account (rev 15, principal asks 22-23/7). Things a master
 * account gets from the ZPCD mirror for free but a per-account S2S app cannot:
 *
 *  1. Test — re-verify the stored Zoom credential live (token + one bare read). Add-time verify
 *     runs once; a secret rotated on Zoom's side afterwards only surfaced when the sweep started
 *     failing. Now it is one click, with last_verified_at bumped and the outcome audited.
 *  2. Account type + Seats (rev 16, 23/7) — a per-account app has no billing/plan scope either, so
 *     these two showed "—" everywhere on Pool Accounts/the Dashboard for every standalone account.
 *     The operator enters them by hand once at setup; account_type is a Select over the same four
 *     tiers tierOf() (lib/planNames.ts) already buckets every mirror-sourced value into, so a
 *     standalone row needs no special-casing anywhere downstream once this is filled in.
 *  3. Zoom subscription end — a per-account app has NO billing scope (verified against the
 *     Marketplace picker), so the "two clocks" dashboard card was structurally blind for
 *     standalone accounts. The operator enters it by hand (once per renewal year); the card reads
 *     it for standalone rows exactly as it reads the mirror for master rows.
 *
 * Server refusals (409 not-standalone, 422 verify cause, 503 no key) render VERBATIM — same rule
 * as every other pool refusal.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiRequestError } from "@/api/client";
import { Button, DateRangePicker, Input, Modal, Select, StatusPill } from "@/components/ui";
import {
  usePoolCredentialTest,
  usePoolSetManualAccountInfo,
  usePoolSetSubscriptionPeriod,
} from "@/features/pool/hooks";
import type { PoolAccount } from "@/api/types";

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? e.error.message : fallback;
}

/** The four tiers tierOf() (lib/planNames.ts) already buckets every mirror-sourced account_type
 *  into — a manual entry is restricted to exactly these (server-whitelisted too) so it round-trips
 *  through that bucketing unchanged, no special-casing needed anywhere this value is displayed. */
const ACCOUNT_TYPE_OPTIONS = ["Pro", "Business", "Enterprise", "Education"] as const;

export function StandaloneAccountDialog({
  account,
  onClose,
}: {
  account: PoolAccount;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const testMut = usePoolCredentialTest();
  const infoMut = usePoolSetManualAccountInfo();
  const subMut = usePoolSetSubscriptionPeriod();
  const [acctType, setAcctType] = useState(account.manual_account_type ?? "");
  const [seats, setSeats] = useState(
    account.manual_seats != null ? String(account.manual_seats) : "",
  );
  const [subStart, setSubStart] = useState(account.manual_subscription_start ?? "");
  const [subEnd, setSubEnd] = useState(account.manual_subscription_end ?? "");
  const [testError, setTestError] = useState<string | null>(null);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [subError, setSubError] = useState<string | null>(null);
  const [testOk, setTestOk] = useState<string | null>(null);

  function runTest() {
    setTestError(null);
    setTestOk(null);
    testMut.mutate(account.account_id, {
      onSuccess: (res) => setTestOk(res.verified_at),
      onError: (e) => setTestError(errMessage(e, t("state.errorTitle"))),
    });
  }

  function saveInfo(accountType: string | null, seatsValue: number | null) {
    setInfoError(null);
    infoMut.mutate(
      { accountId: account.account_id, accountType, seats: seatsValue },
      { onError: (e) => setInfoError(errMessage(e, t("state.errorTitle"))) },
    );
  }

  function savePeriod(start: string | null, end: string | null) {
    setSubError(null);
    subMut.mutate(
      { accountId: account.account_id, subscriptionStart: start, subscriptionEnd: end },
      { onError: (e) => setSubError(errMessage(e, t("state.errorTitle"))) },
    );
  }

  const seatsNum = seats.trim() === "" ? null : Number(seats);
  const seatsValid = seats.trim() === "" || (Number.isInteger(seatsNum) && (seatsNum ?? 0) > 0);
  const infoAnySet = acctType.trim() !== "" || seats.trim() !== "";

  const bothSet = subStart.trim() !== "" && subEnd.trim() !== "";
  const anySet = subStart.trim() !== "" || subEnd.trim() !== "";

  return (
    <Modal
      open
      onClose={onClose}
      title={t("pool.settings.standalone.title", { name: account.account_name ?? "" })}
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t("action.close")}
        </Button>
      }
    >
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-2">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-(length:--text-sm) font-semibold text-(--text-strong)">
              {t("pool.settings.standalone.testTitle")}
            </h3>
            <p className="text-(length:--text-2xs) text-(--text-subtle)">
              {t("pool.settings.standalone.testHint")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" loading={testMut.isPending} onClick={runTest}>
              {t("pool.settings.standalone.testButton")}
            </Button>
            {testOk && (
              <StatusPill tone="ok">
                {String(
                  t("pool.settings.standalone.testOk", {
                    when: new Date(testOk).toLocaleString(),
                  }),
                )}
              </StatusPill>
            )}
          </div>
          {testError && (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {testError}
            </p>
          )}
        </section>

        <section className="flex flex-col gap-2 border-t border-(--border-hairline) pt-5">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-(length:--text-sm) font-semibold text-(--text-strong)">
              {t("pool.settings.standalone.acctInfoTitle")}
            </h3>
            <p className="text-(length:--text-2xs) text-(--text-subtle)">
              {t("pool.settings.standalone.acctInfoHint")}
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
                {t("pool.settings.standalone.acctTypeLabel")}
              </span>
              <Select
                value={acctType}
                onChange={setAcctType}
                aria-label={t("pool.settings.standalone.acctTypeLabel")}
                options={[
                  { value: "", label: t("pool.settings.standalone.acctTypeNotSet") },
                  ...ACCOUNT_TYPE_OPTIONS.map((v) => ({ value: v, label: v })),
                ]}
                className="w-44"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
                {t("pool.settings.standalone.seatsLabel")}
              </span>
              <Input
                type="number"
                min={1}
                step={1}
                value={seats}
                onChange={(e) => setSeats(e.target.value)}
                aria-label={t("pool.settings.standalone.seatsLabel")}
                aria-invalid={!seatsValid}
                className="w-28"
              />
            </label>
            <Button
              size="sm"
              variant="primary"
              loading={infoMut.isPending}
              disabled={!seatsValid}
              aria-label={t("pool.settings.standalone.acctInfoSaveLabel")}
              onClick={() => saveInfo(acctType.trim() === "" ? null : acctType, seatsNum)}
            >
              {t("action.save")}
            </Button>
            {infoAnySet && (
              <Button
                size="sm"
                variant="ghost"
                aria-label={t("pool.settings.standalone.acctInfoClearLabel")}
                onClick={() => {
                  setAcctType("");
                  setSeats("");
                  saveInfo(null, null);
                }}
              >
                {t("pool.settings.standalone.acctInfoClear")}
              </Button>
            )}
          </div>
          {!seatsValid && (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {t("pool.settings.standalone.seatsInvalid")}
            </p>
          )}
          {infoError && (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {infoError}
            </p>
          )}
        </section>

        <section className="flex flex-col gap-2 border-t border-(--border-hairline) pt-5">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-(length:--text-sm) font-semibold text-(--text-strong)">
              {t("pool.settings.standalone.subPeriodTitle")}
            </h3>
            <p className="text-(length:--text-2xs) text-(--text-subtle)">
              {t("pool.settings.standalone.subPeriodHint")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DateRangePicker
              value={{ start: subStart, end: subEnd }}
              onChange={(r) => {
                setSubStart(r.start);
                setSubEnd(r.end);
              }}
              aria-label={t("pool.settings.standalone.subPeriodTitle")}
            />
            <Button
              size="sm"
              variant="primary"
              loading={subMut.isPending}
              disabled={!bothSet}
              onClick={() => savePeriod(subStart, subEnd)}
            >
              {t("action.save")}
            </Button>
            {anySet && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSubStart("");
                  setSubEnd("");
                  savePeriod(null, null);
                }}
              >
                {t("pool.settings.standalone.subEndClear")}
              </Button>
            )}
          </div>
          {subError && (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {subError}
            </p>
          )}
        </section>
      </div>
    </Modal>
  );
}
